// Inquiries (Leads) action factories for DataTableV2
//
// Each factory returns an Action object as defined in DataTableV2/types.js.
// Conditional visibility is handled via `hidden: (row) => boolean` where
// the condition only depends on row data. Feature-flag-gated actions
// (contractsEnabled, isClaim, longtermWaitlistEnabled) are included or
// excluded from the ACTIONS array in the component using spread syntax.

const isPendingOrInProgress = (row) =>
  row.status === "pending" || row.status === "in_progress"

// Primary row action — navigates to the lead message thread
export const ViewLeadAction = ({ marinaSlug }) => ({
  label: "Message",
  icon: "paper-plane",
  iconStyle: "solid",
  primary: true,
  href: (row) => `/manage/${marinaSlug}/messages/${row.encodedId}?type=Lead`,
})

// Always available — navigates to the contact profile
export const ViewCustomerAction = ({ marinaSlug }) => ({
  label: "View Customer",
  icon: "person",
  iconStyle: "solid",
  href: (row) => `/manage/${marinaSlug}/contacts/${row.contact.encodedId}`,
})

// Shown only when status is "pending"
export const InProgressAction = ({ onUpdate }) => ({
  label: "In Progress",
  icon: "handshake",
  iconStyle: "solid",
  onClickSingle: (row) =>
    onUpdate({ id: row.encodedId, status: "in_progress" }),
  hidden: (row) => row.status !== "pending",
})

// Shown when pending or in_progress AND there are contract groups
// Caller is responsible for only including this when contractsEnabled && groups.length > 0
export const CreateContractAction = ({ onCreateContract }) => ({
  label: "Create Contract",
  icon: "add-contract",
  onClickSingle: (row) => onCreateContract(row),
  hidden: (row) => !isPendingOrInProgress(row),
})

// Shown when pending or in_progress
// Caller is responsible for only including this when !isClaim
export const CreateReservationAction = ({ marinaSlug }) => ({
  label: "Create Reservation",
  icon: "add-reservation",
  href: (row) =>
    `/manage/${marinaSlug}/reservations/new/contact/${row.contact.encodedId}/boat/${row.contactBoat.encodedId}?inquiry_id=${row.encodedId}`,
  hidden: (row) => !isPendingOrInProgress(row),
})

// Shown when pending or in_progress
// Caller is responsible for only including this when longtermWaitlistEnabled
export const AddToWaitlistAction = ({ onUpdate }) => ({
  label: "Add to Long-term Waitlist",
  icon: "add-waitlist",
  onClickSingle: (row) => onUpdate({ id: row.encodedId, status: "waitlisted" }),
  hidden: (row) => !isPendingOrInProgress(row),
})

// Shown when pending or in_progress
export const MarkAsCompleteAction = ({ onUpdate }) => ({
  label: "Mark as Complete",
  icon: "contract-circle-check",
  onClickSingle: (row) => onUpdate({ id: row.encodedId, status: "completed" }),
  hidden: (row) => !isPendingOrInProgress(row),
})

// Shown when pending or in_progress
export const DeclineAction = ({ onDecline }) => ({
  label: "Decline",
  icon: "contract-circle-x",
  danger: true,
  onClickSingle: (row) => onDecline(row.encodedId),
  hidden: (row) => !isPendingOrInProgress(row),
})

// Shown when inquiry has been waitlisted and has a waitlist entry
export const ViewWaitlistAction = ({ marinaSlug }) => ({
  label: "View Waitlist",
  icon: "md-list-alt",
  href: (row) =>
    `/manage/${marinaSlug}/messages/${row.longTermWaitlistEntryId}?type=LongTermWaitlistEntry`,
  hidden: (row) => !row.longTermWaitlistEntryId,
})

// Shown when completed with an associated contract
export const ViewContractAction = ({ marinaSlug }) => ({
  label: "View Contract",
  icon: "contract",
  href: (row) =>
    `/manage/${marinaSlug}/contacts/${row.contact.encodedId}#contracts`,
  hidden: (row) => row.status !== "completed" || !row.contractQuoteId,
})

// Shown when completed with a reservation but no contract
export const ViewReservationAction = ({ marinaSlug }) => ({
  label: "View Reservation",
  icon: "md-calendar-month",
  href: (row) =>
    `/manage/${marinaSlug}/contacts/${row.contact.encodedId}#reservations`,
  hidden: (row) =>
    row.status !== "completed" || !!row.contractQuoteId || !row.reservationId,
})
